import { useEffect } from 'react'
import { useAuth } from './useAuth'
import { useActiveMode } from './useActiveMode'
import { supabase } from '../lib/supabase'
import { osLogin, osLogout, osSetTags, osOnForegroundNotification } from '../lib/onesignal'
import { playNotificationSound } from '../lib/notificationSound'
import { subscribeToPush } from '../lib/push'

// useActiveMode(role) → [activeMode, setActiveMode]

// Sincroniza OneSignal con el estado de Bellason:
//   - login(user.id) cuando hay sesión
//   - logout() cuando se cierra sesión
//   - tags { role, active_mode, zones, services } para segmentación
//   - pide permiso de notificaciones la primera vez que hay sesión
//
// Las edge functions del backend van a usar la REST API de OneSignal con
// filtros tipo { tag: role = 'provider' AND tag: zones CONTAINS 'Norte' }
// para mandar el push correcto a quien corresponde.
export function useOneSignalSync() {
  const { user, role } = useAuth()
  const { activeMode } = useActiveMode(role)

  // Sonido al recibir push en foreground (tab activo). En background el SO
  // muestra la notificación nativa con su sonido predeterminado; este hook
  // garantiza que CUALQUIER push también suene con el MP3 cuando la app está
  // abierta. Registra el listener una sola vez al montar.
  useEffect(() => {
    osOnForegroundNotification((event) => {
      const urgent = !!event?.notification?.additionalData?.urgent
      playNotificationSound(urgent)
    })
  }, [])

  // Login / logout + native VAPID push subscription
  useEffect(() => {
    if (user?.id) {
      const uid = user.id
      osLogin(uid)

      // Only subscribe if permission is already granted.
      // If not yet granted, the Feed's "Activar" button handles requestPermission()
      // from a direct user tap (required on Android Chrome) then calls subscribeToPush.
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        subscribeToPush(uid)
      }
    } else {
      osLogout()
    }
  }, [user?.id])

  // Listen for Chrome settings change (denied → granted) without page refresh.
  // PermissionStatus.onchange fires immediately when the user flips the toggle.
  const userId = user?.id
  useEffect(() => {
    if (!userId || typeof navigator?.permissions?.query !== 'function') return
    let ps
    navigator.permissions.query({ name: 'notifications' })
      .then(status => {
        ps = status
        ps.onchange = () => {
          if (Notification.permission === 'granted') {
            osLogin(userId)
            subscribeToPush(userId)
          }
        }
      })
      .catch(() => {})
    return () => { if (ps) ps.onchange = null }
  }, [userId])

  // Re-ensure subscription when app/tab becomes active.
  // This closes the null gap on some mobile browsers after reload/app resume.
  useEffect(() => {
    if (!userId || typeof Notification === 'undefined') return

    const ensure = () => {
      if (Notification.permission === 'granted') {
        subscribeToPush(userId)
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') ensure()
    }

    const timer = setTimeout(ensure, 1500)
    window.addEventListener('focus', ensure)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('focus', ensure)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [userId])

  // Tags: rol, active_mode y user_id para targeting server-side
  // user_id permite enviar push por filtro de tag aunque login() falle
  useEffect(() => {
    if (!user?.id) return
    osSetTags({
      role: role || '',
      active_mode: activeMode || role || '',
      user_id: user.id,
    })
  }, [user?.id, role, activeMode])

  // Tags: zonas + servicios (solo si es provider) — escucha provider_profiles
  useEffect(() => {
    if (!user?.id || role !== 'provider') return () => {}
    let cancelled = false

    async function load() {
      const { data } = await supabase
        .from('provider_profiles')
        .select('zones, services, is_available')
        .eq('user_id', user.id)
        .single()
      if (cancelled || !data) return
      // Set zones como tags individuales (zone_norte, zone_centro, zone_sur)
      // porque OneSignal filter no soporta "contains" en CSV. Cada zona = un tag.
      // Mismo approach para servicios.
      const tags = {
        is_available: data.is_available === false ? 'false' : 'true',
        // CSV combinado se conserva por compatibilidad y debug visual
        zones: data.zones || [],
        services: data.services || [],
      }
      ;(data.zones || []).forEach((z) => {
        tags[`zone_${String(z).toLowerCase()}`] = 'true'
      })
      ;(data.services || []).forEach((s) => {
        tags[`service_${String(s).toLowerCase()}`] = 'true'
      })
      osSetTags(tags)
    }
    load()

    // Refresca si el provider cambia sus zonas / servicios / disponibilidad
    const ch = supabase
      .channel(`os-tags-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'provider_profiles',
        filter: `user_id=eq.${user.id}`,
      }, () => load())
      .subscribe()

    return () => {
      cancelled = true
      supabase.removeChannel(ch)
    }
  }, [user?.id, role])
}
